/**
 * Applies the active filter/sort state to the session question list and returns
 * both the full set and the filtered subset, each with precomputed stats.
 */

import { useMemo } from 'react';
import { useSessionStore } from '@/stores/useSessionStore';
import { useFilterStore } from '@/stores/useFilterStore';
import { filterQuestions } from '../utils/filterQuestions';
import { sortQuestions } from '../utils/sortQuestions';
import type { DdqSessionQuestion, ReviewStats } from '@/types/api.types';

/**
 * Return shape for {@link useFilteredQuestions}.
 *
 * `stats` reflects the entire session; `filteredStats` reflects only the
 * questions that passed the current filter criteria.
 */
export interface UseFilteredQuestionsReturn {
  questions: DdqSessionQuestion[];
  filteredQuestions: DdqSessionQuestion[];
  stats: ReviewStats;
  filteredStats: ReviewStats;
}

function calculateStats(questions: DdqSessionQuestion[]): ReviewStats {
  const stats: ReviewStats = {
    totalQuestions: questions.length,
    exactMatches: 0,
    suggestedMatches: 0,
    generatedResponses: 0,
    noMatches: 0,
    accepted: 0,
    modified: 0,
    rejected: 0,
    pending: 0,
    automationRate: 0,
    completionRate: 0,
  };

  for (const q of questions) {
    // Tier counts
    switch (q.response_tier) {
      case 'Exact':
        stats.exactMatches++;
        break;
      case 'Suggested':
        stats.suggestedMatches++;
        break;
      case 'Generated':
        stats.generatedResponses++;
        break;
      case 'NoMatch':
        stats.noMatches++;
        break;
    }

    // Action counts
    switch (q.userAction) {
      case 'Accepted':
        stats.accepted++;
        break;
      case 'Modified':
        stats.modified++;
        break;
      case 'Rejected':
        stats.rejected++;
        break;
      default:
        stats.pending++;
        break;
    }
  }

  // Calculate rates
  if (stats.totalQuestions > 0) {
    stats.automationRate =
      ((stats.exactMatches + stats.suggestedMatches) / stats.totalQuestions) * 100;
    stats.completionRate =
      ((stats.accepted + stats.modified + stats.rejected) / stats.totalQuestions) * 100;
  }

  return stats;
}

/**
 * Reads the current session questions from `useSessionStore` and the active
 * filter/sort criteria from `useFilterStore`, then returns both the full
 * question list and the filtered+sorted subset along with `ReviewStats` for
 * each.
 *
 * All derived values are memoized; they recompute only when their store
 * dependencies change.
 *
 * @returns The unfiltered questions, the filtered+sorted questions, and
 *   precomputed stats for each set.
 */
export function useFilteredQuestions(): UseFilteredQuestionsReturn {
  const questions = useSessionStore((s) => s.questions);
  const {
    selectedTiers,
    selectedStatuses,
    minConfidence,
    maxConfidence,
    searchQuery,
    sortBy,
  } = useFilterStore();

  const filteredQuestions = useMemo(() => {
    const filtered = filterQuestions(questions, {
      selectedTiers,
      selectedStatuses,
      minConfidence,
      maxConfidence,
      searchQuery,
    });

    return sortQuestions(filtered, sortBy);
  }, [
    questions,
    selectedTiers,
    selectedStatuses,
    minConfidence,
    maxConfidence,
    searchQuery,
    sortBy,
  ]);

  const stats = useMemo(() => calculateStats(questions), [questions]);
  const filteredStats = useMemo(
    () => calculateStats(filteredQuestions),
    [filteredQuestions]
  );

  return {
    questions,
    filteredQuestions,
    stats,
    filteredStats,
  };
}
